import { useQuery } from 'wasp/client/operations';
import { getOrganizationsByTypeKey, getOrganizationTypeByKey } from 'wasp/client/operations';
import {
  Building2,
  MapPin,
  Search,
  Filter,
  Globe,
} from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../client/components/Sidebar';
import { useState, useMemo, useEffect, useCallback } from 'react';
import { useOrgNavMode } from '../../client/contexts/OrgNavModeContext';
import { useLocationContext } from '../../client/contexts/LocationContext';
import { resolveOrgLocation } from './orgLocationResolver';
import { OrganizationDetailListItem } from './OrganizationDetailListItem';
import {
  useResponsiveLayout,
} from '../../components/ui/enhanced-page-layout';

const ORG_TYPE_LABELS: Record<string, string> = {
  'disability-services':   'Disability Services',
  'funeral-services':      'Funeral Services',
  'healthcare-chaplaincy': 'Chaplaincy Services',
  'charities':             'Community Charities',
  'financial-services':    'Community Financial Services',
  'Community-schools':       'Community Schools',
  'social-services':       'Social Services',
};

const ORG_TYPE_TITLES: Record<string, string> = {
  'disability-services':   'Disability Services in Canada',
  'funeral-services':      'Funeral Services in Canada',
  'healthcare-chaplaincy': 'Chaplaincy Services in Canada',
  'charities':             'Community Charities in Canada',
  'financial-services':    'Community Financial Services in Canada',
  'Community-schools':       'Community Schools in Canada',
  'social-services':       'Social Services in Canada',
};

function parseCanadianAddress(address: string | null | undefined): { city: string | null; province: string | null } {
  if (!address) return { city: null, province: null };
  const parts = address.split(',').map((p) => p.trim());
  if (parts.length < 3) return { city: null, province: null };
  const city = parts[parts.length - 2];
  const lastPart = parts[parts.length - 1]; // e.g. "ON M5V 1A1"
  const province = lastPart.split(' ')[0] || null;
  return { city: city || null, province };
}

const SCOPE_LABELS: Record<string, string> = {
  NATIONAL:   'National',
  PROVINCIAL: 'Provincial',
  MUNICIPAL:  'Municipal',
};

// Map URL typeKey to the "organizations" group key used in landing nav configs
const ORG_GROUP_KEYS: Record<string, string> = {
  'disability-services':   'disability_organizations',
  'funeral-services':      'funeral_organizations',
  'healthcare-chaplaincy': 'chaplain_organizations',
  'charities':             'charity_organizations',
  'financial-services':    'ifs_organizations',
  'Community-schools':       'school_organizations',
  'social-services':       'ss_organizations',
};

export default function OrganizationsByTypePage() {
  const { typeKey } = useParams<{ typeKey: string }>();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProvince, setSelectedProvince] = useState('All');
  const [selectedCity, setSelectedCity] = useState('All');
  const [selectedScope, setSelectedScope] = useState('All');

  const [showFilters, setShowFilters] = useState(false);

  const { viewMode, setViewMode, shouldCollapseSidebar, hideViewSwitcher } =
    useResponsiveLayout('table');

  const navigate = useNavigate();
  const { selectedLocation, locations, setSelectedLocation } = useLocationContext();

  // Sync province/city filters with top-menu location selection
  useEffect(() => {
    if (!selectedLocation) return;
    const level = (selectedLocation as any).hierarchyLevel ?? 0;
    if (level === 1) {
      // Province selected
      setSelectedProvince(selectedLocation.name);
      setSelectedCity('All');
    } else if (level === 2) {
      // City selected — also set parent province
      const provinceName = (selectedLocation as any).parentLocation?.name ?? 'All';
      setSelectedProvince(provinceName);
      setSelectedCity(selectedLocation.name);
    } else {
      // Country or neighbourhood — reset
      setSelectedProvince('All');
      setSelectedCity('All');
    }
  }, [selectedLocation?.id]);

  const handleOrgClick = useCallback((org: any) => {
    const match = resolveOrgLocation(org, locations);
    if (match) setSelectedLocation(match as any);
    navigate(`/${org.slug}/information-desk`);
  }, [locations, setSelectedLocation, navigate]);

  // Set landing nav context so CommonSidebar shows the org-type left nav.
  // Clicking a nav item navigates back to the landing page with that section active.
  const { setOrgTypeKey, setOrgNavConfig } = useOrgNavMode();
  useEffect(() => {
    if (!typeKey) return;
    const landingKey = `landing-${typeKey}`;
    const activeKey = ORG_GROUP_KEYS[typeKey] ?? `${typeKey}_organizations`;
    const handleSelect = (key: string) => {
      navigate(`/organization-types/${typeKey}#${key}`);
    };
    setOrgTypeKey(landingKey);
    setOrgNavConfig({ activeKey, enabledKeys: new Set(), onSelect: handleSelect });
    return () => { setOrgTypeKey(null); setOrgNavConfig(null); };
  }, [typeKey, setOrgTypeKey, setOrgNavConfig, navigate]);

  const { data: orgTypeRaw, isLoading: typeLoading } = useQuery(
    getOrganizationTypeByKey,
    { key: typeKey! },
    { enabled: !!typeKey }
  );
  const orgType = orgTypeRaw as any;

  const { data: orgsRaw, isLoading: orgsLoading, error } = useQuery(
    getOrganizationsByTypeKey,
    { typeKey: typeKey! },
    { enabled: !!typeKey }
  );
  const allOrgs: any[] = (orgsRaw as any[]) ?? [];

  const isLoading = typeLoading || orgsLoading;

  const getOrgProvince = (o: any) => o.province?.name ?? parseCanadianAddress(o.address).province ?? null;
  const getOrgCity    = (o: any) => o.city?.name    ?? parseCanadianAddress(o.address).city    ?? null;

  const provinces = useMemo(() => {
    const set = new Set<string>();
    allOrgs.forEach((o) => { const v = getOrgProvince(o); if (v) set.add(v); });
    return Array.from(set).sort();
  }, [allOrgs]);

  const cities = useMemo(() => {
    const set = new Set<string>();
    allOrgs.forEach((o) => {
      if (selectedProvince !== 'All' && getOrgProvince(o) !== selectedProvince) return;
      const v = getOrgCity(o); if (v) set.add(v);
    });
    return Array.from(set).sort();
  }, [allOrgs, selectedProvince]);

  const filtered = useMemo(() => {
    return allOrgs.filter((o) => {
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        if (
          !o.name.toLowerCase().includes(q) &&
          !o.description?.toLowerCase().includes(q) &&
          !(getOrgCity(o)?.toLowerCase().includes(q)) &&
          !(getOrgProvince(o)?.toLowerCase().includes(q))
        ) return false;
      }
      if (selectedProvince !== 'All' && getOrgProvince(o) !== selectedProvince) return false;
      if (selectedCity !== 'All' && getOrgCity(o) !== selectedCity) return false;
      if (selectedScope !== 'All' && (o.scope ?? '') !== selectedScope) return false;
      return true;
    });
  }, [allOrgs, searchTerm, selectedProvince, selectedCity, selectedScope]);

  const typeLabel = orgType?.label ?? ORG_TYPE_LABELS[typeKey!] ?? typeKey;
  const pageTitle = ORG_TYPE_TITLES[typeKey!] ?? `${typeLabel} in Canada`;
  const subtitle = isLoading
    ? ''
    : `${filtered.length} organization${filtered.length !== 1 ? 's' : ''}`;

  return (
    <DashboardLayout
      forceSidebarCollapse={shouldCollapseSidebar}
      showViewSwitcher={!hideViewSwitcher}
      activeView={viewMode}
      onViewChange={setViewMode}
    >
      <div className="w-full flex flex-col">

        {/* Sticky Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200">
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{pageTitle}</h1>
                {subtitle && <p className="text-sm text-gray-600 mt-0.5">{subtitle}</p>}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowFilters(f => !f)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                    showFilters
                      ? 'bg-blue-50 border-blue-300 text-blue-700'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Filter className="w-4 h-4" />
                  Filters
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Sticky Table Column Header — table view only */}
        {viewMode === 'table' && !isLoading && !error && (
          <div className="sticky top-0 z-20 bg-sidebar-hover border-b border-border">
            <div className="px-6 py-3 space-y-3">
              <div className="grid grid-cols-12 gap-4 text-xs font-medium text-content-text-muted uppercase tracking-wider">
                <div className="col-span-4">Organization</div>
                <div className="col-span-2">Province</div>
                <div className="col-span-2">City / Town</div>
                <div className="col-span-2">Neighbourhood</div>
                <div className="col-span-2">Scope</div>
              </div>
              {showFilters && <div className="grid grid-cols-12 gap-4">
                <div className="col-span-4">
                  <input
                    type="text"
                    placeholder="Search organizations..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="col-span-2">
                  <select
                    value={selectedProvince}
                    onChange={(e) => { setSelectedProvince(e.target.value); setSelectedCity('All'); }}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
                  >
                    <option value="All">All Provinces</option>
                    {provinces.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <select
                    value={selectedCity}
                    onChange={(e) => setSelectedCity(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
                  >
                    <option value="All">All Cities</option>
                    {cities.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="col-span-2" />
                <div className="col-span-2">
                  <select
                    value={selectedScope}
                    onChange={(e) => setSelectedScope(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
                  >
                    <option value="All">All Scopes</option>
                    {Object.entries(SCOPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
              </div>}
            </div>
          </div>
        )}

        {/* Content */}
        <div>
          {/* Cards/List search bar */}
          {viewMode !== 'table' && (
            <div className="px-4 pt-4 pb-2">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={`Search ${typeLabel}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          )}

          {/* Loading */}
          {isLoading && (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3" />
                <p className="text-sm text-gray-500">Loading organizations...</p>
              </div>
            </div>
          )}

          {/* Error */}
          {!isLoading && error && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Organizations</h3>
                <p className="text-sm text-gray-500">Please try again later.</p>
              </div>
            </div>
          )}

          {/* Empty state */}
          {!isLoading && !error && filtered.length === 0 && (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <Building2 className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 font-medium">
                  {(searchTerm || selectedProvince !== 'All' || selectedCity !== 'All' || selectedScope !== 'All')
                    ? 'No organizations match your filters'
                    : `No ${typeLabel} organizations found`}
                </p>
                {(searchTerm || selectedProvince !== 'All' || selectedCity !== 'All' || selectedScope !== 'All') && (
                  <button
                    onClick={() => { setSearchTerm(''); setSelectedProvince('All'); setSelectedCity('All'); setSelectedScope('All'); }}
                    className="mt-2 text-sm text-blue-600 hover:text-blue-800"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            </div>
          )}

          {/* ── TABLE VIEW ── */}
          {!isLoading && !error && filtered.length > 0 && viewMode === 'table' && (
            <div className="w-full divide-y divide-gray-200">
              {filtered.map((org: any) => (
                <div
                  key={org.id}
                  onClick={() => handleOrgClick(org)}
                  className="block px-6 py-3 hover:bg-hover transition-colors cursor-pointer"
                >
                  <div className="grid grid-cols-12 gap-4 items-start">
                    <div className="col-span-4 text-sm font-medium text-gray-900 hover:text-blue-600 line-clamp-2">
                      {org.name}
                    </div>
                    <div className="col-span-2 text-sm text-gray-500">
                      {org.province?.name ?? parseCanadianAddress(org.address).province ?? '—'}
                    </div>
                    <div className="col-span-2 text-sm text-gray-500">
                      {org.city?.name ?? parseCanadianAddress(org.address).city ?? '—'}
                    </div>
                    <div className="col-span-2 text-sm text-gray-500 truncate">
                      {org.address ? org.address.split(',')[0]?.trim() ?? '—' : '—'}
                    </div>
                    <div className="col-span-2 text-sm text-gray-500">
                      {org.scope ? SCOPE_LABELS[org.scope] ?? org.scope : '—'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── CARDS VIEW ── */}
          {!isLoading && !error && filtered.length > 0 && viewMode === 'cards' && (
            <div className="px-4 pb-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filtered.map((org: any) => (
                  <div
                    key={org.id}
                    onClick={() => handleOrgClick(org)}
                    className="group bg-white border border-gray-200 rounded-xl p-5 hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0 py-6">
                        <h3 className="font-semibold text-gray-900 group-hover:text-blue-700 transition-colors text-sm leading-snug">
                          {org.name}
                        </h3>
                        {org.description && (
                          <p className="text-xs text-gray-500 mt-2 line-clamp-2 leading-relaxed">
                            {org.description}
                          </p>
                        )}
                        <div className="flex items-center gap-3 mt-3 text-xs text-gray-400">
                          {(org.city?.name || org.province?.code) && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {[org.city?.name, org.province?.code].filter(Boolean).join(', ')}
                            </span>
                          )}
                          {org.scope && (
                            <span>{SCOPE_LABELS[org.scope] ?? org.scope}</span>
                          )}
                          {org.website && (
                            <a href={org.website.startsWith('http') ? org.website : `https://${org.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-600 hover:text-blue-800 transition-colors" onClick={e => e.stopPropagation()}>
                              <Globe className="w-3 h-3" />
                              {((org.website.startsWith("https://") ? org.website.slice(8) : org.website.startsWith("http://") ? org.website.slice(7) : org.website).replace("//", ""))}
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── DETAIL LIST VIEW ── */}
          {!isLoading && !error && filtered.length > 0 && viewMode === 'list' && (
            <div className="pb-2 divide-y divide-gray-200">
              {filtered.map((org: any) => (
                <OrganizationDetailListItem
                  key={org.id}
                  org={org}
                  onSelect={() => handleOrgClick(org)}
                  className="hover:bg-gray-50"
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}



